"use strict";
/**
 * Billing & subscription identifiers.
 *
 * Shared source of truth for RevenueCat entitlement IDs across frontend
 * (`apps/frontend/src/integrations/RevenueCat.ts`) and backend (permission
 * middleware in `apps/backend/functions/src/trpc/permissions.ts`, added in
 * Phase 2 of the paywall stream).
 *
 * Entitlements are stable, semantic gates — they describe access level, not
 * pricing. Products under an entitlement may rotate (price changes spawn new
 * date-stamped product IDs like `stella_premium_monthly_may052026` →
 * `stella_premium_monthly_jul012027`), but the entitlement string itself
 * stays put for the lifetime of the access tier. New tiers (Pro+, Family,
 * etc.) get their own entitlement (`stella_premium_plus`) — they never
 * overwrite an existing one.
 *
 * One entitlement covers both sandbox and production environments. RC's SDK
 * detects environment from the receipt source, and the same entitlement is
 * granted regardless.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.RC_STORE_TO_DB_STORE = exports.RevenueCatWebhookPayloadSchema = exports.RevenueCatWebhookEventSchema = exports.RevenueCatHandledEventTypeSchema = exports.RC_HANDLED_EVENT_TYPES = exports.RevenueCatPeriodTypeSchema = exports.RC_PERIOD_TYPES = exports.CIO_TRIAL_STARTED_EVENT = exports.CIO_SUBSCRIPTION_STATUS = exports.SubscriptionStatusSchema = exports.SubscriptionGrantStatusSchema = exports.GrantTypeSchema = exports.GRANT_TYPES = exports.ENTITLEMENTS = exports.EntitlementIdSchema = exports.ENTITLEMENT_IDS = exports.STELLA_PREMIUM_ENTITLEMENT = void 0;
const zod_1 = require("zod");
/**
 * The active Stella Premium entitlement. Granted via any of the products in
 * the `default` (organic) or `affiliate_<partner>` (attributed) RC offerings.
 */
exports.STELLA_PREMIUM_ENTITLEMENT = 'stella_premium';
/**
 * Canonical list of Stella entitlement IDs. Single source of truth — drives
 * the runtime values, the Zod enum, and the `EntitlementId` union type.
 *
 * **Add new tiers here**, never rename or remove existing IDs. Past
 * subscribers' RC `customerInfo` will continue to reference the original
 * lookup_key forever, so removing an ID would orphan their state. Adding a
 * new entitlement (e.g. `stella_premium_plus`) lights up TypeScript errors at
 * every call site that hasn't mapped it through `permissionsForEntitlements`,
 * which is exactly the safety net we want.
 */
exports.ENTITLEMENT_IDS = [exports.STELLA_PREMIUM_ENTITLEMENT];
exports.EntitlementIdSchema = zod_1.z.enum(exports.ENTITLEMENT_IDS);
/**
 * Object-literal accessor for entitlement IDs. Prefer `STELLA_PREMIUM_ENTITLEMENT`
 * directly when you only need the string; use this map when you want to iterate
 * or reference by symbolic name.
 */
exports.ENTITLEMENTS = {
    STELLA_PREMIUM: exports.STELLA_PREMIUM_ENTITLEMENT,
};
/**
 * Non-store entitlement "grant" kinds, recorded in the `subscription_grant`
 * ledger. One RC promotional-grant primitive powers all of them; the `type`
 * distinguishes UX/analytics/dedup (one `trial` per identity; a `grandfather`
 * grant doesn't consume it). A growing taxonomy — add here with no DB
 * migration (the ledger column is `text` + `$type<GrantType>()`, not a pg
 * enum, matching the `subscription.entitlementId`/`store` convention).
 */
exports.GRANT_TYPES = ['trial', 'grandfather', 'comp', 'partner'];
exports.GrantTypeSchema = zod_1.z.enum(exports.GRANT_TYPES);
/**
 * The user's most-recent non-store grant, surfaced on `getStatus` so the
 * frontend can distinguish a trial from a paid subscription and render the
 * "free trial activated" moment + countdown. `grantedAt` is the recency signal
 * (e.g. "granted in the last hour" → show the activation screen); `expiresAt`
 * drives the countdown. This is the LATEST grant of any type regardless of
 * whether it is still active, so consumers must derive the live trial state:
 *
 *   trialActiveNow =
 *     status.isActive &&
 *     status.grant?.type === 'trial' &&
 *     status.expiresAt?.getTime() === status.grant.expiresAt.getTime()
 *
 * The mirror `expiresAt` equals the grant `expiresAt` while the grant is the
 * thing providing access; once a user buys a paid plan the mirror expiry moves
 * off the (now-stale) trial grant's, so the equality check above stops a
 * previously-trialed, now-paid user from reading as "on trial".
 */
exports.SubscriptionGrantStatusSchema = zod_1.z.object({
    type: exports.GrantTypeSchema,
    grantedAt: zod_1.z.date(),
    expiresAt: zod_1.z.date(),
});
/**
 * The user-facing subscription status shape — produced by the backend
 * `SubscriptionDomain` entitlement reader and returned by the
 * `subscription.getStatus` tRPC procedure.
 *
 * Lives here (not in a backend domain file) so the tRPC router can reference
 * it without dragging the backend Inversify DI graph into the frontend's
 * type-check via tRPC's `AppRouter` inference.
 */
exports.SubscriptionStatusSchema = zod_1.z.object({
    isActive: zod_1.z.boolean(),
    entitlements: zod_1.z.array(exports.EntitlementIdSchema),
    expiresAt: zod_1.z.date().nullable(),
    grant: exports.SubscriptionGrantStatusSchema.nullable(),
});
/**
 * Customer.io person-attribute values for `subscription_status` — the attribute
 * a date-triggered "trial ending / pre-charge" campaign filters on. Const map
 * (not bare string literals) so the backend writer and any future reader agree
 * on the exact tokens.
 *
 *  - `trialing`  — in a store free trial; a charge is scheduled at `trial_charge_at`
 *  - `active`    — converted / paying (the trial charge went through, or a direct buy)
 *  - `cancelled` — cancelled before the charge; the pre-charge email MUST be suppressed
 *  - `expired`   — trial/subscription lapsed without converting
 */
exports.CIO_SUBSCRIPTION_STATUS = {
    TRIALING: 'trialing',
    ACTIVE: 'active',
    CANCELLED: 'cancelled',
    EXPIRED: 'expired',
};
/** Customer.io track-event name emitted when a store free trial begins. */
exports.CIO_TRIAL_STARTED_EVENT = 'trial_started';
/**
 * RevenueCat subscription `period_type` — distinguishes a free trial from a
 * paid/intro period on purchase + renewal events. We branch the trial → CIO
 * sync on `TRIAL`. Added to the webhook event schema (which is otherwise a
 * loose object) so the trial detection is type-safe rather than a stringly-typed
 * lookup on an untyped field.
 */
exports.RC_PERIOD_TYPES = [
    'TRIAL',
    'INTRO',
    'NORMAL',
    'PROMOTIONAL',
];
exports.RevenueCatPeriodTypeSchema = zod_1.z.enum(exports.RC_PERIOD_TYPES);
/**
 * RevenueCat webhook event types (Phase 1 scope — those that affect access).
 *
 * These types change `is_active` state on the subscription mirror. Other
 * RC event types (TEST, SUBSCRIPTION_PAUSED, NON_RENEWING_PURCHASE,
 * INVOICE_ISSUANCE) are accepted by the schema (see RevenueCatWebhookPayloadSchema)
 * but are logged-and-ignored downstream rather than enumerated here.
 *
 * Mapping to `is_active`:
 *   - INITIAL_PURCHASE | RENEWAL | UNCANCELLATION | PRODUCT_CHANGE → true
 *   - CANCELLATION → depends on cancel_reason / expiration_at_ms
 *   - EXPIRATION → false
 *   - BILLING_ISSUE → keeps is_active=true (RC grace period covers the user
 *     until the subscription actually expires)
 *   - TRANSFER | SUBSCRIBER_ALIAS → preserve current state (user identity
 *     change, not entitlement state change)
 */
exports.RC_HANDLED_EVENT_TYPES = [
    'INITIAL_PURCHASE',
    'RENEWAL',
    'CANCELLATION',
    'EXPIRATION',
    'UNCANCELLATION',
    'PRODUCT_CHANGE',
    'BILLING_ISSUE',
    'TRANSFER',
    'SUBSCRIBER_ALIAS',
];
exports.RevenueCatHandledEventTypeSchema = zod_1.z.enum(exports.RC_HANDLED_EVENT_TYPES);
/**
 * Permissive Zod schema for RC v1 webhook payloads.
 *
 * Top-level shape: `{ api_version: '1.0', event: <eventSchema> }`.
 * The event object uses `z.looseObject()` so unknown fields don't fail
 * validation — RC adds new fields without bumping api_version.
 * (Zod 4: `z.looseObject` replaces the deprecated `.passthrough()` method.)
 */
exports.RevenueCatWebhookEventSchema = zod_1.z.looseObject({
    id: zod_1.z.string(),
    type: zod_1.z.string(), // permissive — Phase 1 narrows downstream via RC_HANDLED_EVENT_TYPES
    // Identity/purchase fields are present on purchase-shaped events
    // (INITIAL_PURCHASE, RENEWAL, CANCELLATION, EXPIRATION, …) but ABSENT on
    // identity events: a TRANSFER omits app_user_id entirely and carries
    // `transferred_to` / `transferred_from` instead. `.optional()` / `.nullish()`
    // (not `.nullable()`) so the *key* may be missing, not merely null —
    // otherwise every TRANSFER/SUBSCRIBER_ALIAS/TEST event fails to parse.
    app_user_id: zod_1.z.string().optional(),
    original_app_user_id: zod_1.z.string().optional(),
    product_id: zod_1.z.string().nullish(),
    // RC sends `period_type` on purchase/renewal events (TRIAL | INTRO | NORMAL |
    // PROMOTIONAL). Optional/nullable so non-subscription events that omit it
    // still parse. Drives the trial → Customer.io sync (TRIAL only).
    period_type: exports.RevenueCatPeriodTypeSchema.nullable().optional(),
    cancel_reason: zod_1.z.string().nullable().optional(),
    entitlement_ids: zod_1.z.array(zod_1.z.string()).nullish(),
    purchased_at_ms: zod_1.z.number().nullish(),
    expiration_at_ms: zod_1.z.number().nullish(),
    // TRANSFER events: the app_user_ids the entitlement moved to / from. Used to
    // revoke the donor (`transferred_from`) mirror — see WebhookSubscriptionDomainService.
    transferred_to: zod_1.z.array(zod_1.z.string()).optional(),
    transferred_from: zod_1.z.array(zod_1.z.string()).optional(),
    event_timestamp_ms: zod_1.z.number(),
    store: zod_1.z.enum([
        'APP_STORE',
        'PLAY_STORE',
        'STRIPE',
        'PROMOTIONAL',
        'AMAZON',
        'RC_BILLING',
        'TEST_STORE',
    ]),
    environment: zod_1.z.enum(['PRODUCTION', 'SANDBOX']),
});
exports.RevenueCatWebhookPayloadSchema = zod_1.z.object({
    api_version: zod_1.z.literal('1.0'),
    event: exports.RevenueCatWebhookEventSchema,
});
/**
 * Maps the RC webhook `store` enum to the canonical lower-cased string we
 * persist to the `subscription.store` column. Centralized so backfill,
 * reconciliation, and webhook handling all agree. Values are typed as a
 * literal union so downstream consumers (subscription column `$type<DBStore>`,
 * analytics dashboards) get narrow types instead of generic strings.
 */
exports.RC_STORE_TO_DB_STORE = {
    APP_STORE: 'app_store',
    PLAY_STORE: 'play_store',
    STRIPE: 'stripe',
    PROMOTIONAL: 'promotional',
    AMAZON: 'amazon',
    RC_BILLING: 'rc_billing',
    TEST_STORE: 'test_store',
};
//# sourceMappingURL=billing.js.map